import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin";
import { LexicalTypeaheadMenuPlugin, MenuOption, MenuTextMatch } from "@lexical/react/LexicalTypeaheadMenuPlugin";
import { BRACE_VAR_RE, BraceVarTag, braceVarDisplayName, extractBraceVarRefs, filterClasses, snowyVarCatalog } from "@swivl-mono/helpers";
import {
  $createLineBreakNode,
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $insertNodes,
  $isElementNode,
  $isLineBreakNode,
  $isTextNode,
  COMMAND_PRIORITY_HIGH,
  EditorState,
  KEY_ENTER_COMMAND,
  LexicalNode,
  TextNode
} from "lexical";
import { type JSX, MutableRefObject, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { ErrorText, FormFieldWidth, HelperText, Label, getFormFieldWidthClass } from "../FormField";
import { $createVariableNode, $isVariableNode, VariableNode } from "./VariableNode";

/**
 * VariableTypeahead — a text field with `{{`-triggered variable insertion (ENG-3109).
 *
 * Built fresh on Lexical (Chuck, 2026-08-24; not the tiptap `VariableInput`/`TagInput`).
 * Typing `{{` opens a searchable list of variables by display name; arrows navigate,
 * tab/enter insert. Inserted variables render as pills — the person typing never sees a
 * raw token — while `value` / `onChange` carry plain `{{Type:Key}}` text, which is exactly
 * what the `BraceVars` helper parses. Pasted or typed-out `{{Type:Key}}` tokens become
 * pills automatically.
 */

const MAX_MENU_OPTIONS = 50;
const MAX_QUERY_LENGTH = 60;
/** `{{` followed by a query with no braces, running to the caret. */
const TRIGGER_RE = /\{\{([^{}]*)$/;

export interface VariableTypeaheadProps {
  /** Plain text carrying `{{Type:Key}}` tokens. */
  value?: string;
  onChange?(value: string, name?: string): void;
  /** The variables offered by the typeahead. Defaults to the full `snowyVarCatalog()`. */
  variables?: BraceVarTag[];
  /** Narrows the offered list for this surface (applied to `variables` or the default catalog). */
  filterVariables?: (variable: BraceVarTag) => boolean;
  /** Fires when the `{{` picker opens/closes — hosts (e.g. a dialog) use it to keep Escape from closing themselves. */
  onMenuOpenChange?: (open: boolean) => void;
  name?: string;
  id?: string;
  label?: string;
  helperText?: string;
  error?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  autoFocus?: boolean;
  /** Counted on the stored text (tokens included), not on the pill labels. Exceeding it flags the field; it does not block typing. */
  maxLength?: number;
  onLengthChange?(length: number): void;
  onMaxLengthExceeded?(): void;
  /** `false` turns Enter into `onReturnPressed` instead of a line break. Default `true`. */
  multiline?: boolean;
  onReturnPressed?(): void;
  className?: string;
  containerClassName?: string;
  width?: FormFieldWidth;
  onBlur?(): void;
  onFocus?(): void;
}

class VariableOption extends MenuOption {
  constructor(public readonly tag: BraceVarTag) {
    super(tag.id);
  }
}

type DisplayFor = (id: string) => string;

function $serializeNode(node: LexicalNode): string {
  if ($isVariableNode(node)) return node.getTextContent();
  if ($isLineBreakNode(node)) return "\n";
  if ($isTextNode(node)) return node.getTextContent();
  if ($isElementNode(node)) return node.getChildren().map($serializeNode).join("");
  return "";
}

/** The editor's content as stored text: pills → `{{Type:Key}}`, line breaks → `\n`. */
function $serializeEditor(): string {
  return $getRoot().getChildren().map($serializeNode).join("\n");
}

/** Replace the editor's content with `value`, turning every `{{Type:Key}}` token into a pill. */
function $populateEditor(value: string, displayFor: DisplayFor): void {
  const root = $getRoot();
  root.clear();
  const paragraph = $createParagraphNode();
  const appendText = (text: string): void => {
    text.split("\n").forEach((line, index) => {
      if (index > 0) paragraph.append($createLineBreakNode());
      if (line) paragraph.append($createTextNode(line));
    });
  };
  let last = 0;
  for (const match of value.matchAll(new RegExp(BRACE_VAR_RE.source, "g"))) {
    const start = match.index ?? 0;
    appendText(value.slice(last, start));
    const ref = extractBraceVarRefs(match[0])[0];
    paragraph.append($createVariableNode(match[1], displayFor(match[1]), ref?.fallback ?? null));
    last = start + match[0].length;
  }
  appendText(value.slice(last));
  root.append(paragraph);
}

function triggerFn(text: string): MenuTextMatch | null {
  const match = TRIGGER_RE.exec(text);
  if (!match) return null;
  const query = match[1];
  if (query.length > MAX_QUERY_LENGTH) return null;
  return { leadOffset: match.index, matchingString: query, replaceableString: match[0] };
}

function rankOptions(variables: BraceVarTag[], query: string): VariableOption[] {
  const q = query.trim().toLowerCase();
  const score = (tag: BraceVarTag): number => {
    if (!q) return 0;
    const display = tag.display.toLowerCase();
    const id = tag.id.toLowerCase();
    const idTail = id.includes(":") ? id.slice(id.indexOf(":") + 1) : id;
    if (display.startsWith(q)) return 0;
    // A word of the display name starts with the query ("first" → "User - First Name").
    if (display.split(/[^a-z0-9]+/).some((word) => word.startsWith(q))) return 1;
    if (idTail.startsWith(q)) return 2;
    if (display.includes(q)) return 3;
    if (id.includes(q)) return 4;
    return Number.POSITIVE_INFINITY;
  };
  return (
    variables
      .map((tag) => ({ tag, rank: score(tag) }))
      .filter((entry) => Number.isFinite(entry.rank))
      // Same rank: the shorter display is the more specific match ("First Name" over "First Floor Display Name").
      .sort((a, b) => a.rank - b.rank || a.tag.display.length - b.tag.display.length || a.tag.display.localeCompare(b.tag.display))
      .slice(0, MAX_MENU_OPTIONS)
      .map((entry) => new VariableOption(entry.tag))
  );
}

/** Converts `{{Type:Key}}` text — pasted or typed out in full — into pills as it appears. */
function TokenToPillPlugin({ displayForRef }: { displayForRef: MutableRefObject<DisplayFor> }): null {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    return editor.registerNodeTransform(TextNode, (node) => {
      if (!node.isSimpleText()) return;
      const text = node.getTextContent();
      const match = new RegExp(BRACE_VAR_RE.source, "g").exec(text);
      if (!match) return;
      const start = match.index;
      const end = start + match[0].length;
      let target: TextNode = node;
      if (start === 0 && end < text.length) {
        [target] = node.splitText(end);
      } else if (start > 0) {
        const parts = node.splitText(start, end);
        target = parts[1] ?? node;
      }
      const ref = extractBraceVarRefs(match[0])[0];
      target.replace($createVariableNode(match[1], displayForRef.current(match[1]), ref?.fallback ?? null));
    });
  }, [editor, displayForRef]);
  return null;
}

/** Pushes an external `value` change into the editor (skipping the echo of our own onChange). */
function ValueSyncPlugin({
  value,
  displayForRef,
  lastEmittedRef
}: {
  value: string;
  displayForRef: MutableRefObject<DisplayFor>;
  lastEmittedRef: MutableRefObject<string>;
}): null {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    if (value === lastEmittedRef.current) return;
    lastEmittedRef.current = value;
    editor.update(() => $populateEditor(value, displayForRef.current), { tag: "history-merge" });
  }, [editor, value, displayForRef, lastEmittedRef]);
  return null;
}

function EditableStatePlugin({ editable, autoFocus }: { editable: boolean; autoFocus?: boolean }): null {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    editor.setEditable(editable);
  }, [editor, editable]);
  useEffect(() => {
    if (autoFocus && editable) editor.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);
  return null;
}

/** Single-line mode: Enter fires `onReturnPressed` unless the typeahead menu is open (the menu owns Enter then). */
function SingleLineEnterPlugin({ menuOpenRef, onReturnPressed }: { menuOpenRef: MutableRefObject<boolean>; onReturnPressed?: () => void }): null {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    return editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event) => {
        if (menuOpenRef.current) return false;
        event?.preventDefault();
        onReturnPressed?.();
        return true;
      },
      COMMAND_PRIORITY_HIGH
    );
  }, [editor, menuOpenRef, onReturnPressed]);
  return null;
}

function VariableMenuPlugin({
  variables,
  menuOpenRef,
  menuParent,
  onMenuOpenChange
}: {
  variables: BraceVarTag[];
  menuOpenRef: MutableRefObject<boolean>;
  menuParent?: HTMLElement;
  onMenuOpenChange?: (open: boolean) => void;
}): JSX.Element {
  const [editor] = useLexicalComposerContext();
  const [query, setQuery] = useState<string | null>(null);
  const options = useMemo(() => rankOptions(variables, query ?? ""), [variables, query]);

  const handleSelect = useCallback(
    (option: VariableOption, nodeToReplace: TextNode | null, closeMenu: () => void): void => {
      editor.update(() => {
        const variableNode = $createVariableNode(option.tag.id, option.tag.display);
        if (nodeToReplace) nodeToReplace.replace(variableNode);
        else $insertNodes([variableNode]);
        const spacer = $createTextNode(" ");
        variableNode.insertAfter(spacer);
        spacer.select(1, 1);
      });
      closeMenu();
    },
    [editor]
  );

  return (
    <LexicalTypeaheadMenuPlugin<VariableOption>
      options={options}
      triggerFn={triggerFn}
      onQueryChange={setQuery}
      onSelectOption={handleSelect}
      onOpen={() => {
        menuOpenRef.current = true;
        onMenuOpenChange?.(true);
      }}
      onClose={() => {
        menuOpenRef.current = false;
        onMenuOpenChange?.(false);
      }}
      parent={menuParent}
      menuRenderFn={(anchorElementRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex, options: shown }) => {
        const anchor = anchorElementRef.current;
        if (!anchor) return null;
        // Lexical owns the anchor div (position:absolute, no z-index). Lift IT above modal dialogs
        // (z-500) so the menu is on top for hit-testing too, not just for painting.
        anchor.classList.add("z-600");
        return createPortal(
          <div
            // `relative` so z-600 applies — Lexical's anchor div is positioned but has no z-index, so a
            // static child would stack under a z-500 dialog (found in the SMS campaign compose, 2026-08-27).
            // `pointer-events-auto`: a modal Radix Dialog sets `pointer-events: none` on the body, which a
            // body-portaled menu would inherit — clicks would fall through to the dialog.
            className="pointer-events-auto relative z-600 mt-1 w-72 overflow-hidden rounded-md border border-grey-200 bg-white shadow-lg"
            role="listbox"
            aria-label="Variables"
            data-dialog-safe
          >
            {shown.length === 0 ? (
              <div className="px-3 py-2 text-sm text-grey-500">No matching variables</div>
            ) : (
              <ul className="max-h-64 overflow-y-auto py-1">
                {shown.map((option, index) => (
                  <li
                    key={option.key}
                    ref={(element) => option.setRefElement(element)}
                    role="option"
                    aria-selected={index === selectedIndex}
                    className={filterClasses(
                      "cursor-pointer px-3 py-1.5 text-sm text-grey-900",
                      index === selectedIndex ? "bg-orange-50 text-orange-800" : "hover:bg-grey-50"
                    )}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      selectOptionAndCleanUp(option);
                    }}
                  >
                    {option.tag.display}
                  </li>
                ))}
              </ul>
            )}
          </div>,
          anchor
        );
      }}
    />
  );
}

export function VariableTypeahead(props: VariableTypeaheadProps): JSX.Element {
  const {
    value = "",
    onChange,
    variables: variablesProp,
    filterVariables,
    onMenuOpenChange,
    name,
    id,
    label,
    helperText,
    error,
    required,
    placeholder,
    disabled,
    readOnly,
    autoFocus,
    maxLength,
    onLengthChange,
    onMaxLengthExceeded,
    multiline = true,
    onReturnPressed,
    className,
    containerClassName,
    width = FormFieldWidth.FULL,
    onBlur,
    onFocus
  } = props;

  const generatedId = useId();
  const fieldId = id || generatedId;
  const editable = !disabled && !readOnly;

  // Each surface may pass its own list and/or narrow it; the default is the full catalog.
  const variables = useMemo(() => {
    const base = variablesProp ?? snowyVarCatalog();
    return filterVariables ? base.filter(filterVariables) : base;
  }, [variablesProp, filterVariables]);

  const displayMap = useMemo(() => new Map(variables.map((tag) => [tag.id, tag.display])), [variables]);
  const displayForRef = useRef<DisplayFor>((variableId) => displayMap.get(variableId) ?? braceVarDisplayName(variableId));
  useLayoutEffect(() => {
    displayForRef.current = (variableId) => displayMap.get(variableId) ?? braceVarDisplayName(variableId);
  }, [displayMap]);

  const lastEmittedRef = useRef(value);
  const menuOpenRef = useRef(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [length, setLength] = useState(value.length);
  // The menu is portaled to the document body: Lexical positions its anchor in page
  // coordinates, which go wrong inside a fixed/transformed dialog. Dialogs keep it "inside"
  // via the `data-dialog-safe` marker (see `Modal`), so clicking an option never dismisses them.
  const menuParent: HTMLElement | undefined = undefined;

  const overLimit = maxLength !== undefined && length > maxLength;

  const handleChange = useCallback(
    (editorState: EditorState): void => {
      const serialized = editorState.read(() => $serializeEditor());
      setLength(serialized.length);
      onLengthChange?.(serialized.length);
      if (maxLength !== undefined && serialized.length > maxLength) onMaxLengthExceeded?.();
      if (serialized === lastEmittedRef.current) return;
      lastEmittedRef.current = serialized;
      onChange?.(serialized, name);
    },
    [onChange, name, maxLength, onLengthChange, onMaxLengthExceeded]
  );

  const initialConfig = useMemo(
    () => ({
      namespace: "VariableTypeahead",
      nodes: [VariableNode],
      editable,
      theme: { paragraph: "m-0", variablePill: "inline-block align-baseline" },
      onError: (caught: Error) => {
        throw caught;
      },
      editorState: () => $populateEditor(value, displayForRef.current)
    }),
    // The initial config is read once on mount by design.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const fieldClassName = filterClasses(
    "relative min-h-[40px] w-full rounded-md border bg-white px-3 py-2 text-sm text-grey-900 transition-colors",
    editable && "focus-within:border-orange-500 focus-within:ring-1 focus-within:ring-orange-500",
    error || overLimit ? "border-red-500" : "border-grey-300",
    disabled && "cursor-not-allowed bg-grey-50 text-grey-400",
    className
  );

  return (
    <div className={filterClasses(getFormFieldWidthClass(width), containerClassName)}>
      {label && (
        <Label htmlFor={fieldId} required={required} className="mb-1">
          {label}
        </Label>
      )}
      <LexicalComposer initialConfig={initialConfig}>
        <div ref={wrapperRef} id={fieldId} className={fieldClassName} onBlur={onBlur} onFocus={onFocus}>
          <PlainTextPlugin
            contentEditable={
              placeholder ? (
                <ContentEditable
                  className={filterClasses("min-h-[1.5rem] break-words whitespace-pre-wrap outline-none", !multiline && "whitespace-nowrap")}
                  aria-placeholder={placeholder}
                  placeholder={<div className="pointer-events-none absolute top-2 left-3 text-sm text-grey-400 select-none">{placeholder}</div>}
                />
              ) : (
                <ContentEditable
                  className={filterClasses("min-h-[1.5rem] break-words whitespace-pre-wrap outline-none", !multiline && "whitespace-nowrap")}
                />
              )
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <OnChangePlugin onChange={handleChange} ignoreSelectionChange />
          <TokenToPillPlugin displayForRef={displayForRef} />
          <ValueSyncPlugin value={value} displayForRef={displayForRef} lastEmittedRef={lastEmittedRef} />
          <EditableStatePlugin editable={editable} autoFocus={autoFocus} />
          {!multiline && <SingleLineEnterPlugin menuOpenRef={menuOpenRef} onReturnPressed={onReturnPressed} />}
          {editable && (
            <VariableMenuPlugin variables={variables} menuOpenRef={menuOpenRef} menuParent={menuParent} onMenuOpenChange={onMenuOpenChange} />
          )}
        </div>
      </LexicalComposer>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          {error && <ErrorText>{error}</ErrorText>}
          {!error && helperText && <HelperText>{helperText}</HelperText>}
        </div>
        {maxLength !== undefined && (
          <span className={filterClasses("mt-1 shrink-0 text-xs tabular-nums", overLimit ? "text-red-600" : "text-grey-500")} aria-live="polite">
            {length} / {maxLength}
          </span>
        )}
      </div>
    </div>
  );
}
